import React, { useEffect, useRef, useState } from 'react';
import * as PIXI from 'pixi.js';
import { Live2DModel } from 'pixi-live2d-display/cubism4';

// Expose PIXI to the window object for pixi-live2d-display
(window as any).PIXI = PIXI;

interface Live2DViewerProps {
  modelUrl: string;
  isSpeaking?: boolean;
  emotion?: string;
  emotionLevel?: number;
  isPaused?: boolean;
  onLoad?: () => void;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  onLoadFallback?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// Highly reliable Error Boundary to shield the React engine
class Live2DErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: any, errorInfo: any) {
    console.error("Live2D Engine rendering crashed:", error, errorInfo);
    if (typeof this.props.onLoadFallback === 'function') {
      try {
        this.props.onLoadFallback();
      } catch (e) {}
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="w-full h-full flex flex-col items-center justify-center p-6 bg-slate-101/10 text-center relative z-10 select-none">
          <div className="w-40 h-40 rounded-full bg-pink-50 flex items-center justify-center shadow-lg border-4 border-white animate-pulse mb-4 overflow-hidden relative">
            <img src="/logo.png" alt="Kokoro 2D Fallback" className="w-[110%] h-[110%] object-cover absolute" referrerPolicy="no-referrer" />
          </div>
          <p className="text-xs font-black text-pink-500 uppercase tracking-widest bg-white/80 px-2 py-0.5 rounded-full shadow-sm">Modo 2D Activo</p>
          <p className="text-[10px] text-gray-500 mt-1 max-w-[240px] px-4 font-sans leading-tight">La visualización 3D no está disponible en este navegador. Kokoro sigue acompañándote en modo 2D.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

// Root boundary wrapped export component
export function Live2DViewer(props: Live2DViewerProps) {
  return (
    <Live2DErrorBoundary onLoadFallback={props.onLoad}>
      <Live2DViewerInner {...props} />
    </Live2DErrorBoundary>
  );
}

function Live2DViewerInner({ modelUrl, isSpeaking, emotion = 'alegría', emotionLevel = 1, isPaused = false, onLoad }: Live2DViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const modelRef = useRef<any>(null);
  
  const [initError, setInitError] = useState(false);
  const emotionRef = useRef(emotion);
  const emotionLevelRef = useRef(emotionLevel);
  const isPausedRef = useRef(isPaused);

  useEffect(() => {
    emotionRef.current = emotion;
    emotionLevelRef.current = emotionLevel;
  }, [emotion, emotionLevel]);

  useEffect(() => {
    isPausedRef.current = isPaused;
    if (appRef.current) {
      appRef.current.ticker.speed = isPaused ? 0 : 1;
    }
  }, [isPaused]);

  useEffect(() => {
    if (!containerRef.current || appRef.current) return;

    let isMounted = true;
    let resizeHandler: (() => void) | null = null;
    let pointerHandler: ((e: PointerEvent) => void) | null = null;
    let resetFocusTimeout: number | undefined;
    let focusRaf: number | undefined;

    let app: PIXI.Application;
    try {
      // Initialize PIXI Application safely
      app = new PIXI.Application({
        autoStart: true,
        backgroundAlpha: 0,
        resizeTo: containerRef.current,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });
    } catch (e) {
      console.error("PIXI Application creation failed:", e);
      setInitError(true);
      if (typeof onLoad === 'function') {
        try { onLoad(); } catch (err) {}
      }
      return;
    }
    
    if (isPaused) {
      app.ticker.speed = 0;
    }
    
    try {
      if (app.view) {
        containerRef.current.appendChild(app.view as HTMLCanvasElement);
      }
    } catch (e) {
      console.error("Failed to append PIXI view to container:", e);
      setInitError(true);
      if (typeof onLoad === 'function') {
        try { onLoad(); } catch (err) {}
      }
      return;
    }
    
    appRef.current = app;

    // Load Live2D Model
    Live2DModel.from(modelUrl).then((model) => {
      if (!isMounted || !appRef.current) {
         model.destroy();
         return;
      }
      
      modelRef.current = model;
      app.stage.addChild(model as any);
      
      // Notify parent that the model is fully loaded and ready in user interface
      onLoad?.();

      const originalWidth = model.width;
      const originalHeight = model.height;

      const updateTransform = () => {
        const scaleY = app.screen.height / originalHeight;
        const scaleX = app.screen.width / originalWidth;
        
        const scale = Math.max(scaleX, scaleY) * 1.8;
        
        model.scale.set(scale);
        model.position.set(app.screen.width / 2, app.screen.height / 2 + (originalHeight * scale * 0.18));
      };

      model.anchor.set(0.5, 0.5);
      model.autoInteract = false; // Disable default interaction to use our custom pivot
      updateTransform();

      let targetX = 0;
      let targetY = 0;
      let currentX = 0;
      let currentY = 0;
      let isReturning = false;

      // Custom pointer tracking with a higher pivot point (face level)
      pointerHandler = (e: PointerEvent) => {
          if (isPausedRef.current) return;
          if (!model.internalModel || !model.internalModel.focusController) return;
          
          isReturning = false;

          const emotionCurrent = emotionRef.current.toLowerCase().trim();
          
          if (emotionCurrent === 'tristeza' || emotionCurrent === 'triste' || emotionCurrent === 'sad' || emotionCurrent === 'sadness') {
              // No mueve la cabeza/no sigue el mouse
              targetX = 0;
              targetY = 0; // Neutral forward
              model.internalModel.focusController.focus(targetX, targetY);
              return;
          }
          
          const pivotX = window.innerWidth / 2;
          const pivotY = window.innerHeight * 0.35; // Face level is roughly 35% down
          
          const dx = e.clientX - pivotX;
          const dy = e.clientY - pivotY;

          // Distance and magnitude to make looking natural
          const distance = Math.sqrt(dx * dx + dy * dy);
          const maxDistance = Math.min(window.innerWidth, window.innerHeight) / 2;
          const magnitude = Math.min(1, distance / maxDistance);

          const radian = Math.atan2(dy, dx);
          
          targetX = Math.cos(radian) * magnitude;
          targetY = -Math.sin(radian) * magnitude;
          
          model.internalModel.focusController.focus(targetX, targetY);

          if (resetFocusTimeout) {
             window.clearTimeout(resetFocusTimeout);
          }
          resetFocusTimeout = window.setTimeout(() => {
              isReturning = true;
              targetX = 0;
              targetY = 0;
              // Snatch the current interpolator state
              currentX = model.internalModel!.focusController.x;
              currentY = model.internalModel!.focusController.y;
          }, 1500);
      };
      
      window.addEventListener('pointermove', pointerHandler);

      const updateCustomFocus = () => {
          if (!isMounted) return;
          if (isPausedRef.current) {
              focusRaf = requestAnimationFrame(updateCustomFocus);
              return;
          }

          const emotionCurrent = emotionRef.current.toLowerCase().trim();
          if ((emotionCurrent === 'tristeza' || emotionCurrent === 'triste' || emotionCurrent === 'sad' || emotionCurrent === 'sadness') && model.internalModel && model.internalModel.focusController) {
              const dx = 0 - currentX;
              const dy = 0 - currentY;
              currentX += dx * 0.05;
              currentY += dy * 0.05;
              model.internalModel.focusController.focus(currentX, currentY, true);
          } else if (isReturning && model.internalModel && model.internalModel.focusController) {
              const dx = targetX - currentX;
              const dy = targetY - currentY;
              
              currentX += dx * 0.025; // Slow and delicate interpolation
              currentY += dy * 0.025;
              
              // Use true for instant so we can manually drive the interpolation
              model.internalModel.focusController.focus(currentX, currentY, true); 
              
              if (Math.abs(dx) < 0.001 && Math.abs(dy) < 0.001) {
                  isReturning = false;
              }
          }
          focusRaf = requestAnimationFrame(updateCustomFocus);
      };
      focusRaf = requestAnimationFrame(updateCustomFocus);

      let currentMouthForm = 0;
      let currentEyeSmile = 0;
      let currentBrowY = 0;
      let currentBrowForm = 0;
      let currentEyeOpen = 1;
      let currentLookAway = 0;
      let tristezaBlend = 0;

      const applyEmotion = () => {
         if (isPausedRef.current) return;
         if (!model.internalModel || !model.internalModel.coreModel) return;

         let targetMouthForm = 0;
         let targetEyeSmile = 0;
         let targetBrowY = 0;
         let targetBrowForm = 0;
         let targetEyeOpen = 1;
         let targetLookAway = 0;

         const emotionName = emotionRef.current.toLowerCase().trim();
         const lvl = Math.min(3, Math.max(1, emotionLevelRef.current));
         
         switch(emotionName) {
           case 'alegría':
           case 'alegria':
           case 'feliz':
           case 'happy':
           case 'happiness':
             targetMouthForm = 0.3 * lvl;
             targetBrowY = 0.2 * lvl;
             targetBrowForm = 0.3 * lvl;
             if (lvl === 1) {
               targetEyeOpen = 1.0;
               targetEyeSmile = 0.3;
             } else if (lvl === 2) {
               targetEyeOpen = 0.0;
               targetEyeSmile = 1.0;
               targetMouthForm = 1.0;
               targetBrowY = 0.4;
             } else {
               targetEyeOpen = 0.0;
               targetEyeSmile = 1.0;
               targetMouthForm = 1.0;
             }
             break;
           case 'tristeza':
           case 'triste':
           case 'sad':
           case 'sadness':
             targetMouthForm = -0.4 - (0.1 * lvl);
             targetEyeSmile = 0;
             targetBrowY = 0.2 * lvl; 
             targetBrowForm = 0.5 * lvl;
             if (lvl === 1) {
               targetEyeOpen = 1.0;
             } else if (lvl === 2) {
               targetEyeOpen = 0.8;
             } else {
               targetEyeOpen = 0.6;
             }
             break;
           case 'enojo':
           case 'enojada':
           case 'angry':
           case 'anger':
             targetMouthForm = -0.1 - (0.2 * lvl);
             targetEyeSmile = 0;
             targetBrowY = -0.2 - (0.1 * lvl);
             targetBrowForm = -0.5 - (0.2 * lvl);
             if (lvl === 1) {
               targetEyeOpen = 0.7; // "los ojos así como en tristes dos"
             } else if (lvl === 2) {
               targetEyeOpen = 0.6;
             } else {
               targetEyeOpen = 0.5;
               targetLookAway = 1.0; // Voltea hacia un lado
             }
             break;
           case 'nerviosa':
           case 'nervioso':
           case 'nervous':
           case 'anxious':
             targetMouthForm = -0.1 * lvl;
             targetEyeSmile = 0;
             targetBrowY = 0.1 * lvl;
             targetBrowForm = -0.2 * lvl;
             targetEyeOpen = 1.1;
             break;
           case 'enamorada':
           case 'enamorado':
           case 'in love':
             targetMouthForm = 0.3 * lvl;
             targetEyeSmile = 0.4 * lvl;
             targetBrowY = -0.1 * lvl;
             targetBrowForm = -0.2 * lvl;
             targetEyeOpen = 0.9;
             break;
           default: // Neutral
             targetMouthForm = 0;
             targetEyeSmile = 0;
             targetBrowY = 0;
             targetBrowForm = 0;
             targetEyeOpen = 1;
             break;
         }

         let speed = 0.02;
         if (emotionName === 'alegría' || emotionName === 'alegria' || emotionName === 'feliz' || emotionName === 'happy' || emotionName === 'happiness') {
             speed = 0.1; // Make happiness transitions much faster (snappy)
         }

         // Smooth interpolation
         currentMouthForm += (targetMouthForm - currentMouthForm) * speed;
         currentEyeSmile += (targetEyeSmile - currentEyeSmile) * speed;
         currentBrowY     += (targetBrowY - currentBrowY) * speed;
         currentBrowForm  += (targetBrowForm - currentBrowForm) * speed;
         currentEyeOpen   += (targetEyeOpen - currentEyeOpen) * speed;
         currentLookAway  += (targetLookAway - currentLookAway) * speed;
         
         const core = model.internalModel.coreModel as any;
         core.setParameterValueById('ParamMouthForm', currentMouthForm);
         core.setParameterValueById('ParamEyeLSmile', currentEyeSmile);
         core.setParameterValueById('ParamEyeRSmile', currentEyeSmile);
         core.setParameterValueById('ParamBrowLY', currentBrowY);
         core.setParameterValueById('ParamBrowRY', currentBrowY);
         core.setParameterValueById('ParamBrowLForm', currentBrowForm);
         core.setParameterValueById('ParamBrowRForm', currentBrowForm);

         if (Math.abs(currentEyeOpen - 1) > 0.01) {
            core.setParameterValueById('ParamEyeLOpen', currentEyeOpen);
            core.setParameterValueById('ParamEyeROpen', currentEyeOpen);
         }

         if (currentLookAway > 0.01) {
             core.setParameterValueById('ParamAngleX', currentLookAway * 30);
             core.setParameterValueById('ParamBodyAngleX', currentLookAway * 10);
             core.setParameterValueById('ParamEyeBallX', currentLookAway * 0.8);
             core.setParameterValueById('ParamAngleY', currentLookAway * -10);
         }

         if (emotionName === 'tristeza' || emotionName === 'triste' || emotionName === 'sad' || emotionName === 'sadness') {
             tristezaBlend += (1 - tristezaBlend) * 0.02;
         } else {
             tristezaBlend += (0 - tristezaBlend) * 0.02;
         }

         if (tristezaBlend > 0.01) {
             const angleX = core.getParameterValueById('ParamAngleX');
             core.setParameterValueById('ParamAngleX', angleX * (1 - tristezaBlend));
             
             const angleY = core.getParameterValueById('ParamAngleY');
             core.setParameterValueById('ParamAngleY', angleY * (1 - tristezaBlend) + (-10 * tristezaBlend));
             
             const angleZ = core.getParameterValueById('ParamAngleZ');
             core.setParameterValueById('ParamAngleZ', angleZ * (1 - tristezaBlend));
             
             const bodyAngleX = core.getParameterValueById('ParamBodyAngleX');
             core.setParameterValueById('ParamBodyAngleX', bodyAngleX * (1 - tristezaBlend));
             
             const bodyAngleY = core.getParameterValueById('ParamBodyAngleY');
             core.setParameterValueById('ParamBodyAngleY', bodyAngleY * (1 - tristezaBlend));
             
             const bodyAngleZ = core.getParameterValueById('ParamBodyAngleZ');
             core.setParameterValueById('ParamBodyAngleZ', bodyAngleZ * (1 - tristezaBlend));
             
             const breath = core.getParameterValueById('ParamBreath');
             core.setParameterValueById('ParamBreath', breath * (1 - tristezaBlend));
         }
      };
      
      model.internalModel.on('beforeModelUpdate', applyEmotion);

      // Handle resize
      resizeHandler = () => {
        app.resize();
        updateTransform();
      };
      
      window.addEventListener('resize', resizeHandler);
      model.on('pointerdown', (hitAreas) => {
          // Can play hit animations here
      });
    }).catch((err) => {
      console.error("Failed to load Live2D model:", err);
      // Fallback: trigger onLoad to avoid soft-locking the interface if assets are missing/damaged
      onLoad?.();
    });

    return () => {
      isMounted = false;
      if (resizeHandler) {
          window.removeEventListener('resize', resizeHandler);
      }
      if (pointerHandler) {
          window.removeEventListener('pointermove', pointerHandler);
      }
      if (resetFocusTimeout) {
          window.clearTimeout(resetFocusTimeout);
      }
      if (focusRaf) {
          cancelAnimationFrame(focusRaf);
      }
      if (appRef.current) {
        appRef.current.destroy(true, { children: true });
        appRef.current = null;
      }
    };
  }, [modelUrl]);

  // Realtime mouth simulation if speaking
  useEffect(() => {
      let raf: number;
      let targetMouthOpen = 0;
      let currentMouthOpen = 0;
      let lastChangeTime = 0;

      const updateMouth = (time: number) => {
          if (isPausedRef.current) {
              raf = requestAnimationFrame(updateMouth);
              return;
          }
          if (modelRef.current) {
               if (isSpeaking) {
                   // Change target val every 90ms to simulate average syllable length
                   if (time - lastChangeTime > 90) {
                        targetMouthOpen = Math.random() * 0.8 + 0.2;
                        lastChangeTime = time;
                   }
               } else {
                   targetMouthOpen = 0;
               }

               // Smoothly interpolate towards target
               currentMouthOpen += (targetMouthOpen - currentMouthOpen) * 0.45;
               const core: any = modelRef.current.internalModel.coreModel;
               core.setParameterValueById('ParamMouthOpenY', currentMouthOpen);
          }
          raf = requestAnimationFrame(updateMouth);
      };
      
      raf = requestAnimationFrame(updateMouth);
      
      return () => {
          cancelAnimationFrame(raf);
      };
  }, [isSpeaking]);

  if (initError) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-6 bg-slate-101/10 text-center relative z-10 select-none">
        <div className="w-40 h-40 rounded-full bg-pink-50 flex items-center justify-center shadow-lg border-4 border-white animate-pulse mb-4 overflow-hidden relative">
          <img src="/logo.png" alt="Kokoro 2D Fallback" className="w-[110%] h-[110%] object-cover absolute" referrerPolicy="no-referrer" />
        </div>
        <p className="text-xs font-black text-pink-500 uppercase tracking-widest bg-white/80 px-2 py-0.5 rounded-full shadow-sm">Modo 2D Activo</p>
        <p className="text-[10px] text-gray-500 mt-1 max-w-[240px] px-4 font-sans leading-tight">La visualización 3D no está disponible en este navegador. Kokoro sigue acompañándote de manera interactiva.</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="w-full h-full flex items-center justify-center pointer-events-auto overflow-hidden">
      {/* Canvas will be injected here by PIXI */}
    </div>
  );
}
